import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type {
  OrderWithRelations as BaseOrderWithRelations,
  OrderItem,
  OrderCustomization,
  ConfirmationMatch,
  ParsedConfirmation,
} from "@ckgo2/shared/types";
import { ArrowLeft, User, Mail, Phone, Calendar, Users, ExternalLink } from "lucide-react";
import { formatPrice, formatDate, isOnRequestBooking } from "@ckgo2/shared/utils";
import CommunicationTimeline from "@/components/orders/CommunicationTimeline";
import StatusActions from "@/components/orders/StatusActions";
import { ORDER_STATUS_CONFIG } from "@/lib/statusConfig";
import OrderItemsEditor, { type OrderItemRow } from "@/components/common/OrderItemsEditor";
import ServiceBookingsEditor from "@/components/common/ServiceBookingsEditor";
import ReadinessChecklist from "@/components/common/ReadinessChecklist";
import InvoicePanel from "@/components/orders/InvoicePanel";
import CalculationPanel from "@/components/orders/CalculationPanel";
import PassportPanel from "@/components/orders/PassportPanel";
import DocumentsPanel from "@/components/orders/DocumentsPanel";
import PortalLinkPanel from "@/components/orders/PortalLinkPanel";
import ComplaintsPanel from "@/components/orders/ComplaintsPanel";
import AuditTrailPanel from "@/components/orders/AuditTrailPanel";
import CancellationPanel from "@/components/orders/CancellationPanel";
import BriefButton from "@/components/agent/BriefButton";
import {
  Loader2,
  Save,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  ShieldAlert,
  TrendingUp,
  Sparkles,
  FileText,
  Paperclip,
} from "lucide-react";

// Base type + line items (GET /:id returns them).
type OrderWithRelations = BaseOrderWithRelations & {
  items?: OrderItem[];
  customizations?: OrderCustomization[];
  paidAmount?: number;
};

const WORKFLOW_STEPS = [
  { status: "inquiry", label: "Inquiry" },
  { status: "quoted", label: "Quoted" },
  { status: "confirmed", label: "Confirmed" },
  { status: "deposit_paid", label: "Deposit" },
  { status: "fully_paid", label: "Paid" },
  { status: "completed", label: "Completed" },
];

function getPaymentStatusLabel(order: OrderWithRelations): string {
  if (order.fullPaymentPaidAt) return "Fully paid";
  if (order.depositPaidAt) return "Deposit paid";
  return "Awaiting payment";
}

function OrderWorkflowProgress({ currentStepIndex }: { currentStepIndex: number }) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        {WORKFLOW_STEPS.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;

          return (
            <div key={step.status} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium ${
                    isCompleted
                      ? "bg-success-500 text-white"
                      : isCurrent
                        ? "bg-primary-500 text-white"
                        : "bg-gray-200 text-gray-500 dark:bg-gray-700"
                  }`}
                >
                  {index + 1}
                </div>
                <span
                  className={`mt-2 text-xs font-medium ${
                    isCurrent ? "text-primary-600 dark:text-primary-400" : "text-gray-500"
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {index < WORKFLOW_STEPS.length - 1 && (
                <div
                  className={`mx-2 h-0.5 flex-1 ${
                    index < currentStepIndex ? "bg-success-500" : "bg-gray-200 dark:bg-gray-700"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderTripInfo({ order }: { order: OrderWithRelations }) {
  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Trip</h2>

      <div className="flex gap-4">
        {order.trip?.featuredImage && (
          <img
            src={order.trip.featuredImage}
            alt={order.trip.title}
            className="h-24 w-32 rounded-lg object-cover"
          />
        )}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <h3 className="font-medium text-gray-900 dark:text-white">{order.trip?.title}</h3>
            {order.trip?.slug && (
              <a
                href={`/zajezd/${order.trip.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
          <div className="mt-2 grid gap-2 text-sm text-gray-600 sm:grid-cols-2 dark:text-gray-400">
            {order.tripDate ? (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {formatDate(order.tripDate.startDate)} - {formatDate(order.tripDate.endDate)}
              </div>
            ) : order.requestedDate && order.trip && isOnRequestBooking(order.trip) ? (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Requested: {formatDate(order.requestedDate)}
                {order.trip.durationHours ? (
                  <span className="text-gray-400">· {order.trip.durationHours}h</span>
                ) : null}
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {order.adultsCount || 1} adults
              {order.childrenCount ? `, ${order.childrenCount} children` : ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrderPriceBreakdown({ order }: { order: OrderWithRelations }) {
  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Price breakdown</h2>

      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600 dark:text-gray-400">
            Price x {order.adultsCount || 1} persons
          </span>
          <span className="text-gray-900 dark:text-white">
            {formatPrice(order.totalPrice || 0)}
          </span>
        </div>
        {order.depositAmount && order.depositAmount > 0 && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Deposit</span>
            <span className="text-gray-900 dark:text-white">
              {formatPrice(order.depositAmount)}
            </span>
          </div>
        )}
        <div className="border-t border-gray-200 pt-3 dark:border-gray-700">
          <div className="flex justify-between text-lg font-semibold">
            <span className="text-gray-900 dark:text-white">Total</span>
            <span className="text-primary-600">{formatPrice(order.totalPrice || 0)}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">Deposit</span>
          <span className="text-success-600 font-medium">
            {order.depositPaidAt ? formatPrice(order.depositAmount || 0) : formatPrice(0)}
          </span>
        </div>
        <div className="mt-2 flex justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-400">Status</span>
          <span className="font-medium text-gray-900 dark:text-white">
            {getPaymentStatusLabel(order)}
          </span>
        </div>
      </div>
    </div>
  );
}

function OrderCustomerCard({ order }: { order: OrderWithRelations }) {
  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Customer</h2>

      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 flex h-12 w-12 items-center justify-center rounded-full">
            <User className="h-6 w-6" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {order.customer?.firstName} {order.customer?.lastName}
            </p>
            <Link
              to={`/customers/${order.customerId}`}
              className="text-primary-600 hover:text-primary-700 text-sm"
            >
              View profile
            </Link>
          </div>
        </div>

        <div className="space-y-2 text-sm">
          <a
            href={`mailto:${order.customer?.email}`}
            className="hover:text-primary-600 flex items-center gap-2 text-gray-600 dark:text-gray-400"
          >
            <Mail className="h-4 w-4" />
            {order.customer?.email}
          </a>
          {order.customer?.phone && (
            <a
              href={`tel:${order.customer.phone}`}
              className="hover:text-primary-600 flex items-center gap-2 text-gray-600 dark:text-gray-400"
            >
              <Phone className="h-4 w-4" />
              {order.customer.phone}
            </a>
          )}
        </div>

        {order.customer?.street && (
          <div className="border-t border-gray-200 pt-3 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400">
            <p>{order.customer.street}</p>
            <p>
              {order.customer.zip} {order.customer.city}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function OrderKeyDates({ order }: { order: OrderWithRelations }) {
  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Key dates</h2>

      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600 dark:text-gray-400">Created</span>
          <span className="text-gray-900 dark:text-white">{formatDate(order.createdAt)}</span>
        </div>
        {order.depositDueDate && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Deposit due</span>
            <span className="text-gray-900 dark:text-white">
              {formatDate(order.depositDueDate)}
            </span>
          </div>
        )}
        {order.fullPaymentDue && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Balance due</span>
            <span className="text-gray-900 dark:text-white">
              {formatDate(order.fullPaymentDue)}
            </span>
          </div>
        )}
        {order.tripDate && (
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">Departure</span>
            <span className="text-primary-600 font-medium">
              {formatDate(order.tripDate.startDate)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Where the order came from — source + ad click + UTM (B3.4). Only renders the rows that
 *  exist, so a manual order shows just its source and a lead-converted one shows the click. */
function OrderAttributionCard({ order }: { order: OrderWithRelations }) {
  const rows: Array<[string, string]> = [];
  if (order.source) rows.push(["Source", order.source]);
  if (order.clickId)
    rows.push([
      "Ad click",
      order.clickSource ? `${order.clickSource}: ${order.clickId}` : order.clickId,
    ]);
  if (order.utmSource) rows.push(["UTM source", order.utmSource]);
  if (order.utmMedium) rows.push(["UTM medium", order.utmMedium]);
  if (order.utmCampaign) rows.push(["UTM campaign", order.utmCampaign]);
  if (rows.length === 0) return null;

  return (
    <div className="card">
      <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Attribution</h2>
      <div className="space-y-3 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-3">
            <span className="text-gray-600 dark:text-gray-400">{label}</span>
            <span className="break-all text-right font-medium text-gray-900 dark:text-white">
              {value}
            </span>
          </div>
        ))}
        {order.leadId && (
          <div className="flex justify-between gap-3">
            <span className="text-gray-600 dark:text-gray-400">Lead</span>
            <Link to={`/leads/${order.leadId}`} className="text-primary-600 font-medium">
              View lead
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["order", id],
    queryFn: () => api.get<ApiResponse<OrderWithRelations>>(`/api/admin/orders/${id}`),
  });

  const order = data?.data;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  if (error || !order) {
    return <div className="card text-error-600 text-center">Error loading order.</div>;
  }

  const statusConfig = ORDER_STATUS_CONFIG[order.status] || ORDER_STATUS_CONFIG.inquiry;
  const currentStepIndex = WORKFLOW_STEPS.findIndex((s) => s.status === order.status);
  const isCancelled = order.status === "cancelled";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/orders" className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700">
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Order #{order.id.slice(-8)}
              </h1>
              <span className={`badge ${statusConfig.className}`}>{statusConfig.label}</span>
            </div>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Created: {formatDate(order.createdAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <BriefButton orderId={order.id} />
          <BriefButton orderId={order.id} mode="handover" />
        </div>
      </div>

      {/* Workflow */}
      {!isCancelled && <OrderWorkflowProgress currentStepIndex={currentStepIndex} />}

      {/* Cancelled warning */}
      {isCancelled && (
        <div className="bg-error-50 dark:bg-error-900/20 rounded-lg p-4">
          <p className="text-error-700 dark:text-error-400 font-medium">
            This order has been cancelled
          </p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="space-y-6 lg:col-span-2">
          <OrderTripInfo order={order} />
          <WhatsDifferentCard customizations={order.customizations} />
          <OrderItemsCard order={order} />
          <CalculationPanel orderId={order.id} />
          <ServiceBookingsEditor
            orderId={order.id}
            currency={order.currency}
            pax={(order.adultsCount || 0) + (order.childrenCount || 0)}
          />
          <IncomingConfirmationsCard orderId={order.id} />
          <OrderParticipantsCard order={order} />
          <PassportPanel orderId={order.id} />
          <OrderPriceBreakdown order={order} />
          <InvoicePanel orderId={order.id} />
          <DocumentsPanel orderId={order.id} />
          <ComplaintsPanel orderId={order.id} />
          <AuditTrailPanel orderId={order.id} />

          {order.customerNotes && (
            <div className="card">
              <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                Customer notes
              </h2>
              <p className="text-gray-600 dark:text-gray-400">{order.customerNotes}</p>
            </div>
          )}

          <div className="card">
            <CommunicationTimeline
              orderId={order.id}
              customerId={order.customerId}
              communications={order.communications || []}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <OrderCustomerCard order={order} />

          <div className="card">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Actions</h2>
            <StatusActions
              orderId={order.id}
              currentStatus={order.status}
              totalPrice={order.totalPrice || 0}
              depositAmount={order.depositAmount || 0}
              paidAmount={order.paidAmount || 0}
              tripKind={order.trip?.tripKind}
              fulfillmentMode={order.trip?.fulfillmentMode}
              pax={(order.adultsCount || 0) + (order.childrenCount || 0)}
              tripDateCapacity={order.tripDate?.capacity}
              tripDateBookedCount={order.tripDate?.bookedCount}
              onStatusChange={() => refetch()}
            />
          </div>

          <PortalLinkPanel orderId={order.id} />
          <ReadinessChecklist orderId={order.id} />
          <OrderMarginCard order={order} />
          <OrderKeyDates order={order} />
          <OrderAttributionCard order={order} />
          <CancellationPanel
            orderId={order.id}
            status={order.status}
            cancellationMeta={order.cancellationMeta}
          />
        </div>
      </div>
    </div>
  );
}

// On-read margin (revenue from items − cost from service bookings; B2.1 marginEngine).
function OrderMarginCard({ order }: { order: OrderWithRelations }) {
  const { data } = useQuery({
    queryKey: ["order-margin", order.id],
    queryFn: () =>
      api.get<
        ApiResponse<{
          revenue: number;
          cost: number;
          margin: number;
          marginPct: number;
          currency: string;
        }>
      >(`/api/admin/orders/${order.id}/margin`),
  });
  const m = data?.data;
  if (!m) return null;
  return (
    <div className="card">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <TrendingUp className="h-5 w-5" /> Margin
      </h2>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-500">Revenue</span>
          <span className="text-gray-900 dark:text-white">
            {formatPrice(m.revenue, m.currency)}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Supplier cost</span>
          <span className="text-gray-900 dark:text-white">{formatPrice(m.cost, m.currency)}</span>
        </div>
        <div className="flex justify-between border-t border-gray-200 pt-2 font-semibold dark:border-gray-700">
          <span className="text-gray-700 dark:text-gray-200">Margin</span>
          <span
            className={
              m.margin >= 0
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-red-600 dark:text-red-400"
            }
          >
            {formatPrice(m.margin, m.currency)} ({m.marginPct}%)
          </span>
        </div>
      </div>
    </div>
  );
}

// "What's different about this order" (Block 4.5) — addon/custom items + flagged service deviations,
// derived server-side (order GET). Hidden when the order is a plain standard package.
function WhatsDifferentCard({ customizations }: { customizations?: OrderCustomization[] }) {
  if (!customizations || customizations.length === 0) return null;
  return (
    <div className="card border-l-4 border-l-amber-400">
      <h2 className="mb-1 text-lg font-semibold text-gray-900 dark:text-white">
        What&apos;s different about this order
      </h2>
      <p className="mb-3 text-sm text-gray-500">
        Deviations from the standard package — these flow to the guide.
      </p>
      <ul className="space-y-2">
        {customizations.map((c, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className="mt-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium capitalize text-amber-800">
              {c.source === "service" ? c.kind : c.source}
            </span>
            <span>
              <span className="font-medium">{c.label}</span>
              {c.detail ? <span className="text-gray-500"> — {c.detail}</span> : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface SuggestedAddition {
  name: string;
  grounded: boolean;
  needsPricing: boolean;
}

function OrderItemsCard({ order }: { order: OrderWithRelations }) {
  const queryClient = useQueryClient();
  const [items, setItems] = useState<OrderItemRow[]>(() =>
    (order.items ?? []).map((it) => ({
      id: it.id,
      kind: it.kind,
      tripId: it.tripId,
      tripDateId: it.tripDateId,
      name: it.name,
      description: it.description,
      unitPrice: it.unitPrice,
      quantity: it.quantity,
      currency: it.currency,
      sortOrder: it.sortOrder,
    })),
  );
  const [dirty, setDirty] = useState(false);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [added, setAdded] = useState<SuggestedAddition[] | null>(null);

  const save = useMutation({
    mutationFn: () => api.put(`/api/admin/orders/${order.id}`, { items }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      setDirty(false);
    },
  });

  // "✨ Suggest from notes" (Block 4.6) — AI proposes a full merged item set from the customer's
  // notes (or a pasted transcript). The model extracts intent; the API prices it from the catalog.
  // We load the result into the editor — the admin reviews prices and Saves through the normal PUT.
  const suggest = useMutation({
    mutationFn: (text?: string) =>
      api.post<{ data: { items: OrderItemRow[]; added: SuggestedAddition[]; rationale: string } }>(
        `/api/admin/orders/${order.id}/suggest-items`,
        text ? { text } : {},
      ),
    onSuccess: (r) => {
      setItems(
        r.data.items.map((it, i) => ({
          id: it.id,
          kind: it.kind,
          tripId: it.tripId ?? null,
          tripDateId: it.tripDateId ?? null,
          name: it.name,
          description: it.description ?? null,
          unitPrice: it.unitPrice,
          quantity: it.quantity,
          currency: order.currency,
          sortOrder: it.sortOrder ?? i,
        })),
      );
      setAdded(r.data.added);
      if (r.data.added.length) setDirty(true);
      setShowPaste(false);
      setPasteText("");
    },
  });
  const suggesting = suggest.isPending;

  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Order items</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => suggest.mutate(undefined)}
            disabled={suggesting}
            className="btn-ghost btn-sm disabled:opacity-50"
            title="Propose items from the customer's notes"
          >
            {suggesting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Suggest from notes
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="btn-primary btn-sm disabled:opacity-50"
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save items
          </button>
        </div>
      </div>

      {added && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-700/60 dark:bg-amber-900/20">
          {added.length === 0 ? (
            <span className="text-gray-500 dark:text-gray-400">
              Nothing to add from the notes. Paste a transcript below, or the request had no
              customizations.
            </span>
          ) : (
            <>
              <span className="font-medium text-amber-800 dark:text-amber-200">
                AI proposed {added.length} item{added.length > 1 ? "s" : ""}:
              </span>{" "}
              {added.map((a, i) => (
                <span key={i} className="text-amber-800 dark:text-amber-200">
                  {a.name}
                  {a.needsPricing && (
                    <span className="ml-1 rounded bg-amber-200 px-1 text-xs text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                      set price
                    </span>
                  )}
                  {i < added.length - 1 ? ", " : ""}
                </span>
              ))}
              <span className="ml-1 text-gray-500 dark:text-gray-400">
                — review prices, then Save items.
              </span>
            </>
          )}
        </div>
      )}

      <div className="mb-3">
        <button
          onClick={() => setShowPaste((v) => !v)}
          className="hover:text-brand text-xs text-gray-500 dark:text-gray-400"
        >
          {showPaste ? "Hide" : "Paste call / Plaud notes instead…"}
        </button>
        {showPaste && (
          <div className="mt-2 space-y-2">
            <textarea
              className="input text-sm"
              rows={3}
              placeholder="Paste call notes / a Plaud transcript — e.g. 'wants 5-star hotel, add a Halong sunset cruise for 2'…"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <button
              onClick={() => suggest.mutate(pasteText)}
              disabled={suggesting || !pasteText.trim()}
              className="btn-ghost btn-sm disabled:opacity-50"
            >
              {suggesting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Suggest from this text
            </button>
          </div>
        )}
      </div>

      <OrderItemsEditor
        value={items}
        onChange={(next) => {
          setItems(next);
          setDirty(true);
        }}
        currency={order.currency}
      />
    </div>
  );
}

interface ConfirmationSuggestionRow {
  commId: string;
  attachmentIndex: number;
  filename: string;
  extracted: ParsedConfirmation;
  matches: ConfirmationMatch[];
}

function SuggestionRow({
  s,
  onAttached,
}: {
  s: ConfirmationSuggestionRow;
  onAttached: (bookingId: string) => void;
}) {
  const [bookingId, setBookingId] = useState(s.matches[0]?.serviceBookingId ?? "");
  const attach = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/service-bookings/${bookingId}/confirmation/from-attachment`, {
        commId: s.commId,
        attachmentIndex: s.attachmentIndex,
      }),
    onSuccess: () => onAttached(bookingId),
  });
  const e = s.extracted;
  const summary = [
    e.supplierName,
    e.confirmationNumber && `#${e.confirmationNumber}`,
    e.checkIn && `${e.checkIn}${e.checkOut ? ` → ${e.checkOut}` : ""}`,
    e.amount ? `${e.amount} ${e.currency ?? ""}`.trim() : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <li className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
      <div className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-200">
        <FileText className="h-4 w-4" /> {s.filename}
      </div>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {summary || "Couldn't read fields from this PDF"}
      </p>
      {s.matches.length > 0 ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            className="input max-w-xs text-sm"
            value={bookingId}
            onChange={(ev) => setBookingId(ev.target.value)}
          >
            {s.matches.map((m) => (
              <option key={m.serviceBookingId} value={m.serviceBookingId}>
                {m.bookingLabel} — {m.reason}
              </option>
            ))}
          </select>
          <button
            onClick={() => attach.mutate()}
            disabled={attach.isPending || attach.isSuccess || !bookingId}
            className="btn-ghost btn-sm disabled:opacity-50"
          >
            {attach.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Paperclip className="h-4 w-4" />
            )}
            {attach.isSuccess ? "Attached ✓" : "Attach"}
          </button>
        </div>
      ) : (
        <p className="mt-1 text-xs text-gray-400">No bookings on this order to attach to.</p>
      )}
    </li>
  );
}

// Block 4.6 inbox flow — scan this order's inbound email PDF attachments and one-click attach
// each to the matched booking. AI-cost gated behind an explicit "Scan" (not auto on page load).
function IncomingConfirmationsCard({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient();
  const scan = useMutation({
    mutationFn: () =>
      api.get<{ data: { suggestions: ConfirmationSuggestionRow[]; truncated: boolean } }>(
        `/api/admin/orders/${orderId}/confirmation-suggestions`,
      ),
  });
  const data = scan.data?.data;
  return (
    <div className="card">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Incoming confirmations
        </h2>
        <button
          onClick={() => scan.mutate()}
          disabled={scan.isPending}
          className="btn-ghost btn-sm disabled:opacity-50"
        >
          {scan.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          Scan inbox
        </button>
      </div>
      <p className="mb-3 text-xs text-gray-400">
        AI reads PDF attachments from this order&apos;s emails and suggests which booking each
        confirms.
      </p>
      {data &&
        (data.suggestions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No PDF attachments found on this order&apos;s emails.
          </p>
        ) : (
          <ul className="space-y-2">
            {data.suggestions.map((s, i) => (
              <SuggestionRow
                key={`${s.commId}-${s.attachmentIndex}-${i}`}
                s={s}
                onAttached={(bookingId) =>
                  queryClient.invalidateQueries({ queryKey: ["service-confirmations", bookingId] })
                }
              />
            ))}
          </ul>
        ))}
      {data?.truncated && (
        <p className="mt-2 text-xs text-amber-600">
          More attachments exist than were scanned (showing the first 5).
        </p>
      )}
    </div>
  );
}

interface PaxRow {
  id?: string;
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  nationality: string | null;
  gender: "male" | "female" | "other" | null;
  occupation: string | null;
  passportNumber: string | null;
  passportExpiry: string | null;
  dietary: string | null;
  isLeadPax: boolean;
  sortOrder: number;
  passportMasked?: boolean;
}

const EMPTY_PAX: PaxRow = {
  firstName: "",
  lastName: "",
  birthDate: null,
  nationality: null,
  gender: null,
  occupation: null,
  passportNumber: null,
  passportExpiry: null,
  dietary: null,
  isLeadPax: false,
  sortOrder: 0,
};

// Per-pax editor with GDPR passport masking (Block 2 B2.0.2). Passport numbers come
// masked from the API; revealing them is logged. Editing a passport requires a reveal
// first so a masked value is never saved back over the real one.
function OrderParticipantsCard({ order }: { order: OrderWithRelations }) {
  const queryClient = useQueryClient();
  const [reveal, setReveal] = useState(false);
  const [rows, setRows] = useState<PaxRow[]>([]);
  const [dirty, setDirty] = useState(false);

  const { data } = useQuery({
    queryKey: ["order-participants", order.id, reveal],
    queryFn: () =>
      api.get<{ data: PaxRow[]; canReveal: boolean }>(
        `/api/admin/orders/${order.id}/participants${reveal ? "?reveal=1" : ""}`,
      ),
  });
  const canReveal = data?.canReveal ?? false;

  useEffect(() => {
    if (data?.data && !dirty) setRows(data.data);
  }, [data, dirty]);

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/admin/orders/${order.id}/participants`, {
        participants: rows.map((r, i) => ({ ...r, sortOrder: i })),
      }),
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["order-participants", order.id] });
      queryClient.invalidateQueries({ queryKey: ["order", order.id] });
    },
  });

  const update = (i: number, patch: Partial<PaxRow>) => {
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));
    setDirty(true);
  };
  const addPax = () => {
    setRows((prev) => [...prev, { ...EMPTY_PAX, sortOrder: prev.length }]);
    setDirty(true);
  };
  const removePax = (i: number) => {
    setRows((prev) => prev.filter((_, j) => j !== i));
    setDirty(true);
  };

  // Passport edits need a reveal first (so a masked value isn't saved over the real one).
  const passportEditable = reveal && canReveal;

  return (
    <div className="card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <Users className="h-5 w-5" /> Participants
          <span className="text-sm font-normal text-gray-400">({rows.length})</span>
        </h2>
        <div className="flex items-center gap-2">
          {canReveal && (
            <button
              type="button"
              onClick={() => setReveal((v) => !v)}
              className="btn-ghost btn-sm"
              title={reveal ? "Hide passport numbers" : "Reveal passport numbers (logged)"}
            >
              {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {reveal ? "Hide passports" : "Reveal passports"}
            </button>
          )}
          <button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending}
            className="btn-primary btn-sm disabled:opacity-50"
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </button>
        </div>
      </div>

      {!canReveal && (
        <p className="mb-3 flex items-center gap-1.5 text-xs text-gray-400">
          <ShieldAlert className="h-3.5 w-3.5" /> Passport numbers are restricted for your role.
        </p>
      )}

      <div className="space-y-3">
        {rows.map((p, i) => (
          <div
            key={p.id ?? i}
            className="grid grid-cols-2 gap-2 rounded-lg border border-gray-200 p-3 sm:grid-cols-4 dark:border-gray-700"
          >
            <input
              className="input"
              placeholder="First name"
              value={p.firstName ?? ""}
              onChange={(e) => update(i, { firstName: e.target.value })}
            />
            <input
              className="input"
              placeholder="Last name"
              value={p.lastName ?? ""}
              onChange={(e) => update(i, { lastName: e.target.value })}
            />
            <input
              className="input"
              placeholder="Nationality"
              value={p.nationality ?? ""}
              onChange={(e) => update(i, { nationality: e.target.value })}
            />
            <input
              className="input"
              type="date"
              title="Birth date"
              value={p.birthDate ?? ""}
              onChange={(e) => update(i, { birthDate: e.target.value })}
            />
            <input
              className="input"
              placeholder="Passport no."
              disabled={!passportEditable && !!p.passportMasked}
              value={p.passportNumber ?? ""}
              onChange={(e) => update(i, { passportNumber: e.target.value })}
            />
            <input
              className="input"
              type="date"
              title="Passport expiry"
              disabled={!passportEditable && !!p.passportMasked}
              value={p.passportExpiry ?? ""}
              onChange={(e) => update(i, { passportExpiry: e.target.value })}
            />
            <select
              className="input"
              title="Gender (arrival-card portals require it)"
              value={p.gender ?? ""}
              onChange={(e) => update(i, { gender: (e.target.value || null) as PaxRow["gender"] })}
            >
              <option value="">Gender —</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            <input
              className="input"
              placeholder="Occupation (entry forms)"
              value={p.occupation ?? ""}
              onChange={(e) => update(i, { occupation: e.target.value })}
            />
            <input
              className="input"
              placeholder="Dietary / notes"
              value={p.dietary ?? ""}
              onChange={(e) => update(i, { dietary: e.target.value })}
            />
            <div className="flex items-center justify-between gap-2">
              <label className="flex items-center gap-1.5 text-xs text-gray-500">
                <input
                  type="checkbox"
                  checked={p.isLeadPax}
                  onChange={(e) => update(i, { isLeadPax: e.target.checked })}
                  className="h-3.5 w-3.5 rounded border-gray-300"
                />
                Lead
              </label>
              <button
                type="button"
                onClick={() => removePax(i)}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-700"
                title="Remove participant"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}

        <button type="button" onClick={addPax} className="btn-ghost btn-sm">
          <Plus className="h-4 w-4" />
          Add participant
        </button>
      </div>
    </div>
  );
}
